import React from 'react';

type Listener = () => void;

class GlobalLoadingService {
  private count = 0;
  private messages: string[] = [];
  private listeners = new Set<Listener>();

  start() {
    this.count += 1;
    this.emit();
  }

  stop() {
    this.count = Math.max(0, this.count - 1);
    this.emit();
  }

  getCount() {
    return this.count;
  }

  /**
   * Replace the overlay text while a long operation runs.
   * Returns a cleanup function that must be called once the operation ends.
   */
  pushMessage(message: string) {
    this.messages.push(message);
    this.emit();
    return () => {
      const index = this.messages.lastIndexOf(message);
      if (index !== -1) {
        this.messages.splice(index, 1);
        this.emit();
      }
    };
  }

  getMessage() {
    return this.messages.length > 0 ? this.messages[this.messages.length - 1] : null;
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    listener();
    return () => this.listeners.delete(listener);
  }

  private emit() {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// Kept on globalThis so a hot module replacement in dev cannot split consumers
// (fetch interceptor, Layout, pages) across two instances of the service.
const singletonKey = '__digitaltattooGlobalLoadingService__';
type SingletonHost = typeof globalThis & { [singletonKey]?: GlobalLoadingService };
const singletonHost = globalThis as SingletonHost;

export const globalLoadingService =
  singletonHost[singletonKey] ?? (singletonHost[singletonKey] = new GlobalLoadingService());

export const useGlobalLoadingState = (): { isLoading: boolean; message: string | null } => {
  const [state, setState] = React.useState(() => ({
    isLoading: globalLoadingService.getCount() > 0,
    message: globalLoadingService.getMessage(),
  }));
  React.useEffect(() => {
    const unsubscribe = globalLoadingService.subscribe(() => {
      setState({
        isLoading: globalLoadingService.getCount() > 0,
        message: globalLoadingService.getMessage(),
      });
    });
    return () => {
      unsubscribe();
    };
  }, []);
  return state;
};

export const useGlobalLoading = (): boolean => useGlobalLoadingState().isLoading;
